import { readState } from "@/__main__/app-state.mjs";
import getData from "@/__main__/get-data.mjs";
import type { RouteState } from "@/__main__/router.mjs";
import {
  DEFAULT_SORT_BY,
  LeaderboardMetricsEnum,
  LeaderboardSortParamMap,
  LeaderboardsTabsEnum,
  PLAYER_LEADERBOARD_PAGE_LIMIT,
  SearchParamsEnum,
} from "@/game-eft/constants/constants.mjs";
import fetchProfile from "@/game-eft/fetches/profile.mjs";
import { fetchSeasons } from "@/game-eft/fetches/seasons.mjs";
import type { LeaderboardsPlayer } from "@/game-eft/models/graphql.mjs";
import {
  ItemsValidate,
  LeaderboardsMapsValidate,
  LeaderboardsPlayersValidate,
  LeaderboardsWeaponsValidate,
} from "@/game-eft/models/graphql.mjs";
import * as API from "@/game-eft/utils/api.mjs";
import { devError } from "@/util/dev.mjs";
import { filterErrState } from "@/util/eval-state.mjs";
import keyInObject from "@/util/key-in-object.mjs";

// DataLake / DataBricks data has a TTL of 24 hours
const EXPIRY = 1000 * 60 * 60 * 24; // 24 hours
const ASYNC_REQUEST_LIMIT = 10; // The amount we can asynchronously request safely without crashing

export default async function fetchData(
  [tab]: [
    | LeaderboardsTabsEnum.Players
    | LeaderboardsTabsEnum.Arena
    | LeaderboardsTabsEnum.Maps
    | LeaderboardsTabsEnum.Weapons,
  ],
  searchParams: URLSearchParams,
  state: RouteState,
): Promise<void> {
  try {
    const seasons = await fetchSeasons();
    const seasonId =
      searchParams.get(SearchParamsEnum.Season) ?? seasons[0]?.id;
    const sortBy =
      searchParams.get(SearchParamsEnum.Leaderboard) ?? DEFAULT_SORT_BY;
    const rankingMetric =
      keyInObject(LeaderboardSortParamMap, sortBy) &&
      !LeaderboardSortParamMap[sortBy]?.disabled
        ? LeaderboardSortParamMap[sortBy].value
        : LeaderboardMetricsEnum.SUM_RAIDS;
    if (!seasonId || !seasons.find((i) => i.id === seasonId))
      throw new Error("EFT Leaderboards page requires a valid season id");
    switch (tab) {
      case LeaderboardsTabsEnum.Players: {
        const page = (state.transient?.page as number) || 1;
        if (page > PLAYER_LEADERBOARD_PAGE_LIMIT) return;
        await getData(
          API.getLeaderboardsPlayers({
            faction: "ALL",
            gameMode: "DEFAULT",
            page: String(page),
            seasonId,
            rankingMetric,
          }),
          LeaderboardsPlayersValidate,
          ["eft", "leaderboards", "players", sortBy, page],
          { expiryTime: Date.now() + EXPIRY },
        );
        const data: { [page: string]: Array<LeaderboardsPlayer> } =
          readState.eft.leaderboards.players?.[sortBy];
        for (const page in data) {
          const value = data[page];
          if (!filterErrState(value) || !Array.isArray(value)) continue;
          const length = Math.ceil(value.length / ASYNC_REQUEST_LIMIT);
          for (let i = 0; i < length; i += 1) {
            const profiles = value
              .slice(i * ASYNC_REQUEST_LIMIT, (i + 1) * ASYNC_REQUEST_LIMIT)
              .map((i) =>
                fetchProfile(i.account_id).catch((e) => {
                  devError(
                    `Failed to fetch player profile for ${i.account_id}`,
                    e,
                  );
                }),
              );
            await Promise.all(profiles);
          }
        }
        break;
      }
      case LeaderboardsTabsEnum.Weapons: {
        await Promise.all([
          getData(
            API.getLeaderboardsWeapons({
              faction: "ALL",
              gameMode: "DEFAULT",
              rank: "ALL",
              seasonId,
            }),
            LeaderboardsWeaponsValidate,
            ["eft", "leaderboards", "weapons"],
            { expiryTime: Date.now() + EXPIRY },
          ),
          getData(API.getItems(), ItemsValidate, ["eft", "items"]),
        ]);
        break;
      }
      case LeaderboardsTabsEnum.Maps: {
        await getData(
          API.getLeaderboardsMaps({
            faction: "ALL",
            gameMode: "DEFAULT",
            seasonId,
          }),
          LeaderboardsMapsValidate,
          ["eft", "leaderboards", "maps"],
          { expiryTime: Date.now() + EXPIRY },
        );
        break;
      }
      case LeaderboardsTabsEnum.Arena: {
        await Promise.resolve(); // Game mode doesn't exist yet for closed beta testing
        break;
      }
    }
  } catch (e) {
    devError(`Failed to fetch EFT leaderboards tab (${tab}) data`, e);
  }
}
